/**
 * 공지사항 상세/등록 페이지
 *
 * - id === 'new' : 신규 작성 모드 (isEditing 초기값 true)
 * - 그 외        : 상세 조회/수정 모드
 *
 * 공지사항 데이터는 목록에서 react-router state로 전달받는다.
 * 수정 성공 후에는 페이지 이동 없이 history.state를 직접 갱신하여
 * 브라우저 뒤로가기 시에도 최신 데이터가 유지되도록 한다.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { noticeService } from '../../services/noticeService';
import { useState, useEffect } from 'react';
import { Notice } from '../../types/notice';
import ConfirmModal from '../../components/ui/ConfirmModal';
import { InputField, SelectField, TextareaField } from '../../components/ui/FormField';
import DetailActionButtons from '../../components/ui/DetailActionButtons';
import BackButton from '../../components/ui/BackButton';
import EmptyDetailState from '../../components/ui/EmptyDetailState';
import { formatDate } from '../../utils/format';

export default function NoticeDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const isNewNotice = id === 'new';
  const [isEditing, setIsEditing] = useState(isNewNotice);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const locationNotice = location.state?.notice as Notice | undefined;
  const notice = isNewNotice ? null : locationNotice;

  const [formData, setFormData] = useState<Partial<Notice>>({
    title: '',
    content: '',
    isPinned: false,
    isVisible: true,
  });

  useEffect(() => {
    if (notice && !isNewNotice) {
      setFormData(notice);
    }
  }, [notice, isNewNotice]);

  const createMutation = useMutation({
    mutationFn: (data: Omit<Notice, 'id' | 'createdAt' | 'updatedAt'>) =>
      noticeService.createNotice(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notices'] });
      navigate('/admin/notice');
    },
    onError: () => {
      setIsSubmitting(false);
      alert('공지사항 작성에 실패했습니다. 다시 시도해주세요.');
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: Partial<Notice>) =>
      noticeService.updateNotice(Number(id), data),
    onSuccess: (updatedNotice) => {
      queryClient.invalidateQueries({ queryKey: ['notices'] });
      setIsEditing(false);
      setIsSubmitting(false);
      setFormData(updatedNotice);
      window.history.replaceState(
        { ...window.history.state, notice: updatedNotice },
        ''
      );
    },
    onError: () => {
      setIsSubmitting(false);
      alert('공지사항 수정에 실패했습니다. 다시 시도해주세요.');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => noticeService.deleteNotice(Number(id)),
    onSuccess: () => navigate('/admin/notice'),
  });

  const handleEdit = () => {
    setIsEditing(true);
    if (notice) setFormData(notice);
  };

  const handleSave = () => {
    if (isSubmitting || createMutation.isPending || updateMutation.isPending) return;
    if (!formData || typeof formData !== 'object') {
      alert('데이터가 올바르지 않습니다. 페이지를 새로고침해주세요.');
      return;
    }
    if (!formData.title || !formData.content) {
      alert('제목과 내용을 입력해주세요.');
      return;
    }
    setIsSubmitting(true);
    const payload = {
      title: formData.title,
      content: formData.content,
      isPinned: formData.isPinned || false,
      isVisible: formData.isVisible ?? true,
    };
    if (isNewNotice) {
      createMutation.mutate(payload);
    } else {
      updateMutation.mutate(payload);
    }
  };

  const isSaving = isSubmitting || createMutation.isPending || updateMutation.isPending;

  if (!notice && !isNewNotice && !formData.title) {
    return (
      <EmptyDetailState
        message="공지사항 데이터를 찾을 수 없습니다"
        backTo="/admin/notice"
      />
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">
            {isNewNotice ? '공지사항 작성' : '공지사항 상세'}
          </h1>
          <p className="text-gray-500 mt-2">
            {!isNewNotice && id ? `공지사항 #${id}` : '새 공지사항 작성'}
          </p>
        </div>
        <BackButton to="/admin/notice" />
      </div>

      {/* Notice Info Card */}
      {!isNewNotice && formData?.title && (
        <div className="bg-primary rounded-xl shadow-lg p-8 mb-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center space-x-3 mb-2">
                {formData?.isPinned && (
                  <span className="px-3 py-1 bg-marker-orange text-white text-sm font-bold rounded-full">
                    <i className="fas fa-thumbtack mr-1"></i>고정
                  </span>
                )}
                {!formData.isVisible && (
                  <span className="px-3 py-1 bg-gray-400 text-gray-900 text-sm font-bold rounded-full">
                    <i className="fas fa-eye-slash mr-1"></i>비공개
                  </span>
                )}
                <h2 className="text-3xl font-bold">{formData.title}</h2>
              </div>
              {formData.createdAt && formData.updatedAt && (
                <div className="flex items-center space-x-6 text-white/80 mt-4">
                  <span>
                    <i className="fas fa-calendar-alt mr-2"></i>
                    작성: {formatDate(formData.createdAt)}
                  </span>
                  <span>
                    <i className="fas fa-clock mr-2"></i>
                    수정: {formatDate(formData.updatedAt)}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Form */}
      <div className="bg-white rounded-xl shadow-lg p-8">
        <div className="space-y-6">
          <InputField
            label={<><i className="fas fa-heading text-primary mr-2"></i>제목</>}
            value={formData.title || ''}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            disabled={!isEditing}
            placeholder="제목을 입력하세요"
          />

          <TextareaField
            label={<><i className="fas fa-align-left text-primary mr-2"></i>내용</>}
            value={formData.content || ''}
            onChange={(e) => setFormData({ ...formData, content: e.target.value })}
            disabled={!isEditing}
            rows={12}
            placeholder="내용을 입력하세요"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <SelectField
              label={<><i className="fas fa-thumbtack text-primary mr-2"></i>상단 고정</>}
              value={formData.isPinned ? 'true' : 'false'}
              onChange={(e) => setFormData({ ...formData, isPinned: e.target.value === 'true' })}
              disabled={!isEditing}
            >
              <option value="false">일반</option>
              <option value="true">고정</option>
            </SelectField>

            <SelectField
              label={<><i className="fas fa-eye text-primary mr-2"></i>표시 여부</>}
              value={formData.isVisible ? 'true' : 'false'}
              onChange={(e) => setFormData({ ...formData, isVisible: e.target.value === 'true' })}
              disabled={!isEditing}
            >
              <option value="true">공개</option>
              <option value="false">비공개</option>
            </SelectField>
          </div>

          {!isNewNotice && formData.createdAt && formData.updatedAt && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <InputField
                label={<><i className="fas fa-calendar-plus text-primary mr-2"></i>작성일</>}
                value={formatDate(formData.createdAt)}
                disabled
              />
              <InputField
                label={<><i className="fas fa-calendar-check text-primary mr-2"></i>수정일</>}
                value={formatDate(formData.updatedAt)}
                disabled
              />
            </div>
          )}
        </div>

        <DetailActionButtons
          isNew={isNewNotice}
          isEditing={isEditing}
          isSaving={isSaving}
          saveLabel={isNewNotice ? '작성하기' : '저장하기'}
          onEdit={handleEdit}
          onSave={handleSave}
          onCancelEdit={() => {
            setIsEditing(false);
            if (notice) setFormData(notice);
          }}
          onDelete={() => setShowDeleteModal(true)}
        />
      </div>

      {showDeleteModal && (
        <ConfirmModal
          title="공지사항을 삭제하시겠습니까?"
          confirmLabel="삭제"
          isLoading={deleteMutation.isPending}
          onConfirm={() => { deleteMutation.mutate(); setShowDeleteModal(false); }}
          onCancel={() => setShowDeleteModal(false)}
        />
      )}
    </div>
  );
}
